import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from '../../utils/toast';
import { getMoneyFlow } from '../../services/api';
import { EmptyState } from './emptyState';
import { Skeleton } from './skeleton';
import {
  MoneyFlowPointDto,
  MoneyFlowRange,
  MoneyFlowResponseDto,
} from '../../types/types';

export const RANGE_OPTIONS: { value: MoneyFlowRange; label: string }[] = [
  { value: 'ALL', label: 'all' },
  { value: 'WEEK', label: 'seven_days' },
  { value: 'MONTH', label: 'one_month' },
];

const LINE_COLOR = '#0080FF';
const AREA_TOP_COLOR = '#2563EB';
const AREA_BOTTOM_COLOR = '#EFF6FF';
const VERTICAL_LINE_COLOR = '#2563EB';
const GRID_LINES = [0, 1, 2, 3, 4];
const MAX_X_LABELS = 8;
const REVEAL_DURATION_MS = 800;
const REVEAL_EASING = 'cubic-bezier(0.22, 1, 0.36, 1)';
const GLIDE_CLASSES = 'transition-[left,top] duration-150 ease-out motion-reduce:transition-none';

interface ChartPoint {
  x: number;
  y: number;
}

const getYAxis = (values: number[]) => {
  const lowest = Math.min(...values);
  const highest = Math.max(...values);

  const axisMin = Math.floor(lowest / 100) * 100;
  let axisMax = Math.ceil(highest / 100) * 100;

  if (axisMax === axisMin) {
    axisMax = axisMin + 100;
  }

  const stepSize = (axisMax - axisMin) / 4;
  const ticks = [0, 1, 2, 3, 4].map((stepNumber) => axisMin + stepSize * stepNumber);

  return { axisMin, axisMax, ticks };
};

// Backend sends income and expense only, so net is derived here when it's missing
const getNet = (point: MoneyFlowPointDto): number =>
  point.net ?? (point.income ?? 0) - (point.expense ?? 0);

const getPointX = (index: number, totalPoints: number): number =>
  ((index + 0.5) / totalPoints) * 100;

const getPointY = (value: number, axisMin: number, axisMax: number): number =>
  ((axisMax - value) / (axisMax - axisMin)) * 100;

const getLinePath = (points: ChartPoint[]): string => {
  let path = `M ${points[0].x} ${points[0].y}`;

  for (let i = 1; i < points.length; i++) {
    const previousPoint = points[i - 1];
    const currentPoint = points[i];
    const middleX = (previousPoint.x + currentPoint.x) / 2;

    path += ` C ${middleX} ${previousPoint.y}, ${middleX} ${currentPoint.y}, ${currentPoint.x} ${currentPoint.y}`;
  }

  return path;
};

const getAreaPath = (points: ChartPoint[]): string => {
  const firstPoint = points[0];
  const lastPoint = points[points.length - 1];

  return `${getLinePath(points)} L ${lastPoint.x} 100 L ${firstPoint.x} 100 Z`;
};

const formatNumber = (value: number): number => Number(value.toFixed(2));

interface MoneyFlowTooltipProps {
  point: MoneyFlowPointDto;
  x: number;
  y: number;
  isOnLeft: boolean;
}

const MoneyFlowTooltip: React.FC<MoneyFlowTooltipProps> = ({ point, x, y, isOnLeft }) => {
  const { t } = useTranslation('dashboard');

  return (
    <div
      className={`pointer-events-none absolute z-10 flex w-40 flex-col gap-2 rounded-3xl bg-white px-6 py-4 shadow-[0px_14px_28px_0px_rgba(0,0,0,0.08),0px_-6px_12px_0px_rgba(0,0,0,0.03),0px_2px_8px_0px_rgba(0,0,0,0.06)] ${GLIDE_CLASSES}`}
      style={{
        left: `${x}%`,
        top: `${Math.min(Math.max(y, 25), 75)}%`,
        transform: isOnLeft ? 'translate(calc(-100% - 17px), -50%)' : 'translate(17px, -50%)',
      }}
    >
      <div className="flex items-center justify-between gap-2.5">
        <span className="text-xs font-normal leading-4 text-gray-900">{t('income')}</span>
        <span className="text-base font-normal leading-6 text-success">
          {formatNumber(point.income)}
        </span>
      </div>
      <div className="flex items-center justify-between gap-2.5">
        <span className="text-xs font-normal leading-4 text-gray-900">{t('expense')}</span>
        <span className="text-base font-bold leading-6 text-danger">
          {formatNumber(point.expense)}
        </span>
      </div>
      <div className="flex items-center justify-between gap-2.5">
        <span className="text-xs font-normal leading-4 text-gray-900">{t('deals')}</span>
        <span className="text-base font-bold leading-6 text-gray-900">{point.deals}</span>
      </div>
    </div>
  );
};

export const MoneyFlowChart: React.FC = () => {
  const { t } = useTranslation('dashboard');

  const [range, setRange] = useState<MoneyFlowRange>('WEEK');
  const [moneyFlow, setMoneyFlow] = useState<MoneyFlowResponseDto | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);
  const chartRef = useRef<SVGSVGElement>(null);

  useEffect(() => {
    let isCancelled = false;

    const loadMoneyFlow = async () => {
      setIsLoading(true);
      try {
        const data = await getMoneyFlow(range);
        if (!isCancelled) {
          setMoneyFlow(data);
        }
      } catch {
        if (!isCancelled) {
          setMoneyFlow(null);
          toast.error(t('failed_to_load_money_flow'));
        }
      } finally {
        if (!isCancelled) {
          setIsLoading(false);
        }
      }
    };

    loadMoneyFlow();

    return () => {
      isCancelled = true;
    };
  }, [range, t]);

  useLayoutEffect(() => {
    const chart = chartRef.current;
    const prefersReducedMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;

    if (!chart || typeof chart.animate !== 'function' || prefersReducedMotion) {
      return;
    }

    chart.animate(
      [
        { clipPath: 'inset(-8px 100% -8px 0)' },
        { clipPath: 'inset(-8px -8px -8px -8px)' },
      ],
      { duration: REVEAL_DURATION_MS, easing: REVEAL_EASING }
    );
  }, [moneyFlow]);

  const points = moneyFlow?.points ?? [];
  const hasPoints = points.length > 0;
  const isEmpty = !isLoading && points.every((point) => point.deals === 0);

  const currencySymbol =
    !moneyFlow?.currency || moneyFlow.currency === 'EUR' ? '€' : moneyFlow.currency;
  const totalText = `${(moneyFlow?.totalIncome ?? 0).toFixed(2)} ${currencySymbol}`;

  const { axisMin, axisMax, ticks } = hasPoints
    ? getYAxis(points.map(getNet))
    : { axisMin: 0, axisMax: 100, ticks: [] as number[] };

  const chartPoints = points.map((point, index) => ({
    x: getPointX(index, points.length),
    y: getPointY(getNet(point), axisMin, axisMax),
  }));

  const linePoints = hasPoints
    ? [
        { x: 0, y: chartPoints[0].y },
        ...chartPoints,
        { x: 100, y: chartPoints[chartPoints.length - 1].y },
      ]
    : [];

  const labelStep = Math.ceil(points.length / MAX_X_LABELS);

  const hoveredPoint = hoveredIndex !== null ? points[hoveredIndex] : null;
  const hoveredChartPoint = hoveredIndex !== null ? chartPoints[hoveredIndex] : null;
  const isTooltipOnLeft = hoveredIndex !== null && hoveredIndex >= points.length / 2;

  return (
    <div className="rounded-3xl bg-white p-4 shadow-sm ring-1 ring-black/5 font-['Plus_Jakarta_Sans',sans-serif]">
      <div className="flex flex-col gap-4">
        <p className="text-sm font-medium leading-5 text-gray-900">{t('money_flow')}</p>

        <div className="flex items-start justify-between gap-4">
          <p className="min-w-0 truncate text-4xl font-extrabold leading-10 text-gray-900">
            {totalText}
          </p>

          <div className="inline-flex w-fit shrink-0 items-center gap-0.5 rounded-3xl bg-gray-100 px-2 py-1">
            {RANGE_OPTIONS.map(({ value, label }) => (
              <button
                key={value}
                type="button"
                onClick={() => setRange(value)}
                className={`rounded-3xl px-3 py-1.5 text-sm font-medium leading-5 transition-colors ${
                  range === value
                    ? 'bg-white text-gray-900 shadow-[0px_2px_8px_0px_rgba(0,0,0,0.06)]'
                    : 'text-gray-500 hover:text-gray-800'
                }`}
              >
                {t(label)}
              </button>
            ))}
          </div>
        </div>
      </div>

      {isEmpty ? (
        <div className="mt-6 flex h-72 items-center justify-center">
          <EmptyState text={t('no_data_to_display')} />
        </div>
      ) : (
        <div className="mt-6 flex h-72 flex-col gap-2">
          <div className="flex flex-1 gap-4">
            <div className="flex w-8 flex-col justify-between text-right">
              {[...ticks].reverse().map((tick) => (
                <span key={tick} className="text-xs font-medium leading-4 text-gray-900">
                  {formatNumber(tick)}
                </span>
              ))}
            </div>

            <div className="relative flex-1">
              <div className="absolute inset-x-0 bottom-2 top-2">
                <div className="absolute inset-0 flex flex-col justify-between">
                  {GRID_LINES.map((lineNumber) => (
                    <div key={lineNumber} className="h-0 border-t border-neutral-300" />
                  ))}
                </div>

                {!hasPoints && <Skeleton className="absolute inset-0 rounded-2xl" />}

                {hasPoints && (
                  <svg
                    ref={chartRef}
                    className="absolute inset-0 h-full w-full overflow-visible"
                    viewBox="0 0 100 100"
                    preserveAspectRatio="none"
                  >
                    <defs>
                      <linearGradient id="moneyFlowFill" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor={AREA_TOP_COLOR} stopOpacity="0.3" />
                        <stop offset="100%" stopColor={AREA_BOTTOM_COLOR} stopOpacity="0.3" />
                      </linearGradient>
                    </defs>
                    <path d={getAreaPath(linePoints)} fill="url(#moneyFlowFill)" />
                    <path
                      d={getLinePath(linePoints)}
                      fill="none"
                      stroke={LINE_COLOR}
                      strokeWidth="4"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      vectorEffect="non-scaling-stroke"
                    />
                  </svg>
                )}

                <div className="absolute inset-0 flex" onMouseLeave={() => setHoveredIndex(null)}>
                  {points.map((point, index) => (
                    <div
                      key={point.date}
                      className="h-full flex-1"
                      onMouseEnter={() => setHoveredIndex(index)}
                    />
                  ))}
                </div>

                {hoveredPoint && hoveredChartPoint && (
                  <>
                    <div
                      className={`pointer-events-none absolute bottom-0 w-0.5 -translate-x-1/2 ${GLIDE_CLASSES}`}
                      style={{
                        left: `${hoveredChartPoint.x}%`,
                        top: `${hoveredChartPoint.y}%`,
                        backgroundColor: VERTICAL_LINE_COLOR,
                      }}
                    />
                    <div
                      className={`pointer-events-none absolute size-6 -translate-x-1/2 -translate-y-1/2 rounded-full border-4 border-white ${GLIDE_CLASSES}`}
                      style={{
                        left: `${hoveredChartPoint.x}%`,
                        top: `${hoveredChartPoint.y}%`,
                        backgroundColor: LINE_COLOR,
                      }}
                    />
                    <MoneyFlowTooltip
                      point={hoveredPoint}
                      x={hoveredChartPoint.x}
                      y={hoveredChartPoint.y}
                      isOnLeft={isTooltipOnLeft}
                    />
                  </>
                )}
              </div>
            </div>
          </div>

          <div className="relative ml-12 h-4">
            {points.map((point, index) =>
              index % labelStep === 0 ? (
                <span
                  key={point.date}
                  className="absolute -translate-x-1/2 whitespace-nowrap text-xs font-medium leading-4 text-gray-900"
                  style={{ left: `${chartPoints[index].x}%` }}
                >
                  {point.label}
                </span>
              ) : null
            )}
          </div>
        </div>
      )}
    </div>
  );
};